/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import axios from "axios";
import config from "../config/profileCard.config.json";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { GET_MY_PROFILE } from "../graphql";
import { VApplication } from "../../../application";
import { capitalCase } from "change-case";

import { AppNavigatorService } from "../../appNavigatorService";

const getWhoamiSrv = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.AppNavigatorService") as AppNavigatorService;
};

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface IMyProfileCardMethods extends IPluginMethodMap {
  getMyProfile(args: any): Promise<any>;
}

export class MyProfileCardService implements IAgentPlugin {
  readonly methods: IMyProfileCardMethods;

  constructor() {
    this.methods = {
      getMyProfile: this.getMyProfile.bind(this),
    };
  }

  async getMyProfile(args: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    try {
      if (client) {
        const res = await client.query({
          query: GET_MY_PROFILE,
          fetchPolicy: "network-only",
        });
        if (res?.data) {
          console.log(`profile data`, res.data);
          const _data: any = await this.getFormattedProfile(
            res.data?.getMyProfile
          );
          return {
            ...res.data,
            // ...(_data || {}),
            // tweetyProps: this.formatFormProps(
            //   config.TWEETY_PROPS,
            //   res.data?.getMyProfile
            // ),
          };
        }
      }
    } catch (err: any) {
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
    // return {...(config || {})}
  }

  private async getFormattedProfile(data: any) {
    let _profileInfo: any = {
      title: null,
      subtitle: null,
      image: data?.image ? data?.image : "",
      description: data?.bio,
      listItems: [
        {
          key: "email",
          title: "Email",
          subTitle: data?.email,
          icon: "https://cdn-icons-png.flaticon.com/512/2989/2989993.png",
        },
        {
          key: "phoneNumber",
          title: "Contact No.",
          subTitle: data?.primaryContactNo,
          icon: "https://i.ibb.co/ZY9vKwL/phone-call.png",
        },
        {
          key: "dob",
          title: "DOB",
          subTitle: data?.dob,
          icon: "https://cdn.iconscout.com/icon/premium/png-256-thumb/birth-date-1092585.png",
        },
        {
          key: "gender",
          title: "Gender",
          subTitle: data?.gender,
          icon: "https://cdn-icons-png.flaticon.com/512/83/83674.png",
        },
        {
          key: "milesCard",
          title: "Caribbean Miles",
          subTitle: data?.milesCard,
          icon: "https://cdn-icons-png.flaticon.com/512/83/83674.png",
        },
        {
          key: "address1",
          title: "Address Line 1",
          subTitle: data?.address?.addressLine1,
          icon: "https://icons.veryicon.com/png/o/application/love-app/receiving-address-6.png",
        },
        {
          key: "address2",
          title: "Address Line 2",
          subTitle: data?.address?.addressLine2,
          icon: "https://icons.veryicon.com/png/o/application/love-app/receiving-address-6.png",
        },
        {
          key: "address3",
          title: "Address Line 3",
          subTitle: data?.address?.addressLine3,
          icon: "https://icons.veryicon.com/png/o/application/love-app/receiving-address-6.png",
        },
        {
          key: "city",
          title: "City",
          subTitle: data?.address?.city,
          icon: "https://spng.pngfind.com/pngs/s/77-775332_city-icon-png-city-icon-transparent-png.png",
        },
        {
          key: "state",
          title: "State",
          subTitle: data?.address?.state,
          icon: "https://cdn-icons-png.flaticon.com/512/5149/5149050.png",
        },
        {
          key: "country",
          title: "Country",
          subTitle: data?.address?.country,
          icon: "https://cdn2.iconfinder.com/data/icons/travel-178/100/Country-01-512.png",
        },
        {
          key: "zipcode",
          title: "Zip Code",
          subTitle: data?.address?.zipcode,
          icon: "https://cdn-icons-png.flaticon.com/512/5599/5599544.png",
        },
      ],
      buttons: [
        {
          key: "edit",
          title: "Update Profile",
          color: "primary",
          isPresent: false,
        },
      ],
      initialData: {
        firstName: data?.firstName,
        id: data?.id,
        lastName: data?.lastName,
        avatar: data?.image,
        phoneNumber: data?.primaryContactNo,
        gender: data?.gender,
        dob: data?.dob,
        address1: data?.address?.addressLine1,
        address2: data?.address?.addressLine2,
        milesCard: data?.milesCard,
        address3: data?.address?.addressLine3,
        city: data?.address?.city,
        state: data?.address?.state,
        country: data?.address?.country,
        zipcode: data?.address?.zipcode,
      },
    };
    if (data?.firstName || data?.lastName) {
      const _first_name = data?.firstName ? capitalCase(data?.firstName) : "";
      const _last_name = data?.lastName ? capitalCase(data?.lastName) : "";
      _profileInfo.title = `${_first_name} ${_last_name}`;
    }
    // if (data?.description) {
    //   _profileInfo.subtitle = data?.description;
    // }
    // _profileInfo.profileInfo?.map((item: any, index: number) => {
    //   if (item?.label === "Email") {
    //     item.value = data?.email;
    //   } else if (item?.label === "Address1") {
    //     item.value = data?.address?.addressLine1;
    //   } else if (item?.label === "Address2") {
    //     item.value = data?.address?.addressLine2;
    //   } else if (item?.label === "Address3") {
    //     item.value = data?.address?.addressLine3;
    //   } else if (item?.label === "city") {
    //     item.value = data?.address?.city;
    //   } else if (item?.label === "state") {
    //     item.value = data?.address?.state;
    //   }  else if (item?.label === "country") {
    //     item.value = data?.address?.country;
    //   } else if (item?.label === "zipcode") {
    //     item.value = data?.address?.zipcode;
    //   } else if (item?.label === "DOB") {
    //     item.value = data?.dob;
    //   }
    //   return item;
    // });
    return _profileInfo;
  }

  private formatFormProps(formProps: any, data: any) {
    let _res: any = formProps;
    _res.initialData = {
      firstName: data?.firstName,
      lastName: data?.lastName,
      phoneNumber: data?.primaryContactNo,
      avatar: data?.image,
      gender: data?.gender,
      dob: data?.dob,
      address1: data?.address?.addressLine1,
      milesCard: data?.milesCard,
      address2: data?.address?.addressLine2,
      address3: data?.address?.addressLine3,
      city: data?.address?.city,
      state: data?.address?.state,
      country: data?.address?.country,
      zipcode: data?.address?.zipcode,
    };

    return _res;
  }

  private async isEditEnabled() {
    const srv = getWhoamiSrv();
    const user = await srv?.whoAmI();
    console.log(`user in profile service`, user);
    let formatted = true;
    if (user?.whoami === "admin") {
      formatted = false;
    }
    return formatted;
  }
}
